import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Calendar.scss';

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  startTime?: string;
  endTime?: string;
  type?: 'pending' | 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  color?: string;
}

interface CalendarProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  initialMonth?: number; // 1-12 para enero-diciembre
}

const Calendar: React.FC<CalendarProps> = ({ events, onEventClick, onDateClick, initialMonth }) => {
  const getInitialDate = () => {
    if (initialMonth && initialMonth >= 1 && initialMonth <= 12) {
      const date = new Date();
      date.setMonth(initialMonth - 1); // JavaScript months are 0-indexed
      return date;
    }
    return new Date();
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate());

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isToday = (date: Date) => {
    return isSameDay(date, new Date());
  };

  const getEventsForDay = (date: Date) => {
    return events.filter(event => isSameDay(new Date(event.date), date));
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day calendar-day--empty" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDay(date);
      const today = isToday(date);

      days.push(
        <div
          key={day}
          className={`calendar-day ${today ? 'calendar-day--today' : ''} ${dayEvents.length > 0 ? 'calendar-day--has-events' : ''}`}
          onClick={() => onDateClick?.(date)}
        >
          <div className="calendar-day__number">{day}</div>
          {dayEvents.length > 0 && (
            <div className="calendar-day__events">
              {dayEvents.slice(0, 3).map(event => (
                <div
                  key={event.id}
                  className={`calendar-event calendar-event--${event.type || 'scheduled'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick?.(event);
                  }}
                  title={`${event.title}${event.startTime ? ` - ${event.startTime}` : ''}`}
                >
                  <span className="calendar-event__time">{event.startTime}</span>
                  <span className="calendar-event__title">{event.title}</span>
                </div>
              ))}
              {dayEvents.length > 3 && (
                <div className="calendar-event-more">
                  +{dayEvents.length - 3} más
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="calendar-header__left">
          <h2 className="calendar-header__title">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        <div className="calendar-header__right">
          <button className="calendar-nav-btn" onClick={goToToday}>
            Hoy
          </button>
          <button className="calendar-nav-btn calendar-nav-btn--icon" onClick={previousMonth}>
            <ChevronLeft size={20} />
          </button>
          <button className="calendar-nav-btn calendar-nav-btn--icon" onClick={nextMonth}>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        {/* Day names header */}
        {dayNames.map(day => (
          <div key={day} className="calendar-weekday">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {renderCalendarDays()}
      </div>
    </div>
  );
};

export default Calendar;
